import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Row, Collapse } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ProfileContext } from 'pages/Profile';
import StandardModal from 'components/general/defaultModals/StandardModal';
import { loadConfig } from '../../config';
import { dismissAnnouncementById, readDismissedAnnouncementIds, restoreAnnouncementById } from 'utils/announcementDismissal';
import { canAccessAudience, isPathwaysEligible } from 'utils/acl';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split('/');
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  return null;
};

const formatShortDate = (value) => {
  const parsed = toDate(value);
  if (!parsed) return 'TBA';
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatLongDate = (value) => {
  const parsed = toDate(value);
  if (!parsed) return 'TBA';
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const parseMeetingDateTime = (meetingDate, meetingTime) => {
  if (!meetingDate || typeof meetingDate !== 'string') return null;
  const date = meetingDate.trim();
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(date)) return null;

  const [day, month, year] = date.split('/').map(Number);
  let hours = 0;
  let minutes = 0;

  if (typeof meetingTime === 'string') {
    const timeMatch = meetingTime.trim().match(/^(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      hours = Number(timeMatch[1]) || 0;
      minutes = Number(timeMatch[2]) || 0;
    }
  }

  const target = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return Number.isNaN(target.getTime()) ? null : target;
};

const daysLeftLabel = (meetingDate, meetingTime, now) => {
  const target = parseMeetingDateTime(meetingDate, meetingTime);
  if (!target) return '';

  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return '0d 0h left';

  const dayMs = 24 * 60 * 60 * 1000;
  const hourMs = 60 * 60 * 1000;
  const days = Math.floor(diffMs / dayMs);
  const hours = Math.floor((diffMs % dayMs) / hourMs);

  return `${days}d ${hours}h left`;
};

const normalizeText = (value) => String(value || '').trim();

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value._id !== undefined && value._id !== null) return String(value._id);
    if (value.id !== undefined && value.id !== null) return String(value.id);
    if (value.$oid !== undefined && value.$oid !== null) return String(value.$oid);
    if (typeof value.toString === 'function') {
      const candidate = value.toString();
      if (candidate && candidate !== '[object Object]') return String(candidate);
    }
  }
  return String(value);
};

const isCompletedUserPathway = (userPathway) => {
  const status = normalizeText(userPathway?.status).toLowerCase();
  return status === 'completed' || Boolean(userPathway?.completedAt);
};

const getFeedbackText = (feedback) => normalizeText(feedback?.description || feedback?.text || '');

const normalizeEvaluationTopicName = (value) => {
  const base = normalizeText(value);
  if (!base) return 'Topic';
  return base.replace(/\s*\([^)]*\)/g, '').trim() || 'Topic';
};

const sanitizeManualList = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => normalizeText(item))
    .filter(Boolean);
};

const renderStars = (count) => {
  const safe = Math.max(0, Math.min(5, Math.round(Number(count) || 0)));
  return (
    <span className="evaluation-stars" aria-label={`${safe} of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <span
          key={index}
          className={index < safe ? 'evaluation-star evaluation-star--filled' : 'evaluation-star evaluation-star--empty'}
          aria-hidden="true"
        >
          {index < safe ? '★' : '☆'}
        </span>
      ))}
    </span>
  );
};

const CardReloadButton = ({ loading, onClick, label }) => (
  <button
    type="button"
    className="profile-overview-reload-btn"
    onClick={onClick}
    disabled={loading}
    aria-label={`Reload ${label}`}
    title={`Reload ${label}`}
  >
    <i className={`bi bi-arrow-clockwise${loading ? ' profile-overview-reload-icon--spinning' : ''}`} aria-hidden="true" />
  </button>
);

const TableTopicsEvaluationDetails = ({ evaluation }) => (
  <>
    <h5 className="mb-1">
      {normalizeText(evaluation?.speakerRole || 'Table Topics')}
      {evaluation?.tableTopicsName ? ` - ${normalizeText(evaluation.tableTopicsName)}` : ''}
    </h5>
    <p className="profile-overview-meta mb-2">
      Evaluator: {normalizeText(evaluation?.evaluatorName || 'Table Topics Evaluator')}
    </p>
    {[
      ['What Went Well', evaluation?.commend],
      ['Recommend', evaluation?.recommend],
      ['Overall', evaluation?.overall],
    ].filter(([, text]) => normalizeText(text)).map(([label, text]) => (
      <p key={label} className="mb-2" style={{ whiteSpace: 'pre-line' }}>
        <strong>{label}:</strong> {text}
      </p>
    ))}
  </>
);

export default function Overview() {
  const { profile } = useContext(ProfileContext);
  const { user: authUser } = useSelector(x => x.auth);
  const navigate = useNavigate();
  const url = process.env.REACT_APP_URL;
  const profileUserId = String(profile?._id || '');
  const authUserId = String(authUser?.user?._id || authUser?.user?.id || '');
  const canViewTableTopicsEvaluations = Boolean(
    profileUserId
    && authUser?.user
    && (profileUserId === authUserId || authUser.user.isWebmaster)
  );

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextMeeting, setNextMeeting] = useState(null);
  const [nextAssignment, setNextAssignment] = useState(null);
  const [meetingsConfig, setMeetingsConfig] = useState(null);
  const [allPathways, setAllPathways] = useState([]);
  const [userPathways, setUserPathways] = useState([]);
  const [projectsByPathway, setProjectsByPathway] = useState({});
  const [feedbackProjectsByPathway, setFeedbackProjectsByPathway] = useState({});
  const [evaluationProjectsByPathway, setEvaluationProjectsByPathway] = useState({});
  const [suggestionProjectsByPathway, setSuggestionProjectsByPathway] = useState({});
  const [contestAwards, setContestAwards] = useState([]);
  const [tableTopicsEvaluations, setTableTopicsEvaluations] = useState([]);
  const [tableTopicsEvaluationPreview, setTableTopicsEvaluationPreview] = useState(null);
  const [tableTopicsEvaluationsError, setTableTopicsEvaluationsError] = useState('');
  const [tableTopicsEvaluationsListError, setTableTopicsEvaluationsListError] = useState('');
  const [tableTopicsEvaluationsHasMore, setTableTopicsEvaluationsHasMore] = useState(false);
  const [tableTopicsEvaluationsPage, setTableTopicsEvaluationsPage] = useState(0);
  const [tableTopicsEvaluationsLoadingMore, setTableTopicsEvaluationsLoadingMore] = useState(false);
  const [attendanceBusy, setAttendanceBusy] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [activeAssignmentIndex, setActiveAssignmentIndex] = useState(0);
  const [activeFeedbackIndex, setActiveFeedbackIndex] = useState(0);
  const [activePathwayProjectIndex, setActivePathwayProjectIndex] = useState(0);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAwardsModal, setShowAwardsModal] = useState(false);
  const [showTableTopicsEvaluationsModal, setShowTableTopicsEvaluationsModal] = useState(false);
  const [tableTopicsEvaluationsSearch, setTableTopicsEvaluationsSearch] = useState('');
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [awardsSearch, setAwardsSearch] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [overviewAnnouncements, setOverviewAnnouncements] = useState([]);
  const [selectedOverviewAnnouncement, setSelectedOverviewAnnouncement] = useState(null);
  const [collapsedOverviewAnnouncementIds, setCollapsedOverviewAnnouncementIds] = useState([]);
  const [sectionLoading, setSectionLoading] = useState({
    announcements: true,
    nextMeeting: true,
    nextAssignment: true,
    awards: true,
    tableTopicsEvaluations: true,
    feedback: true,
    evaluation: true,
    pathwayProjects: true,
  });
  const tableTopicsEvaluationsRequestId = useRef(0);

  const toggleOverviewAnnouncementCollapse = (announcementId) => {
    const normalizedId = normalizeId(announcementId);
    if (!normalizedId) return;

    setCollapsedOverviewAnnouncementIds((currentIds) => (
      currentIds.includes(normalizedId)
        ? currentIds.filter((id) => id !== normalizedId)
        : [...currentIds, normalizedId]
    ));
  };

  const handleDismissOverviewAnnouncement = (announcementId) => {
    const normalizedId = normalizeId(announcementId);
    if (!normalizedId) return;

    dismissAnnouncementById(normalizedId);
    setOverviewAnnouncements((currentAnnouncements) => currentAnnouncements.filter((announcement) => normalizeId(announcement?._id) !== normalizedId));
    setCollapsedOverviewAnnouncementIds((currentIds) => currentIds.filter((id) => id !== normalizedId));

    if (normalizeId(selectedOverviewAnnouncement?._id) === normalizedId) {
      setSelectedOverviewAnnouncement(null);
      setShowAnnouncementModal(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const authHeaders = useMemo(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (authUser?.token) {
      headers.Authorization = `Bearer ${authUser.token}`;
    }
    return headers;
  }, [authUser?.token]);

  const markSectionsLoaded = useCallback((sectionNames) => {
    setSectionLoading((prev) => {
      const next = { ...prev };
      sectionNames.forEach((name) => {
        next[name] = false;
      });
      return next;
    });
  }, []);

  const loadTableTopicsEvaluations = useCallback(async ({ page = 0, search = '', append = false } = {}) => {
    if (!profile?._id || !canViewTableTopicsEvaluations) return;
    const requestId = append
      ? tableTopicsEvaluationsRequestId.current
      : tableTopicsEvaluationsRequestId.current + 1;
    if (!append) {
      tableTopicsEvaluationsRequestId.current = requestId;
      setSectionLoading(current => ({ ...current, tableTopicsEvaluations: true }));
      setTableTopicsEvaluationsListError('');
      if (!search.trim()) setTableTopicsEvaluationsError('');
    } else {
      setTableTopicsEvaluationsLoadingMore(true);
    }
    try {
      const params = new URLSearchParams({ page: String(page), search });
      const response = await fetch(
        `${url}/table-topics/evaluations/${encodeURIComponent(profile._id)}?${params.toString()}`,
        { headers: authHeaders }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.Error || `Unable to load Table Topics Evaluations (${response.status}).`);
      }
      const payload = await response.json();
      if (requestId !== tableTopicsEvaluationsRequestId.current) return;
      const items = Array.isArray(payload?.items) ? payload.items : [];
      setTableTopicsEvaluations(current => append ? [...current, ...items] : items);
      if (!append && !search.trim()) setTableTopicsEvaluationPreview(items[0] || null);
      setTableTopicsEvaluationsHasMore(Boolean(payload?.hasMore));
      setTableTopicsEvaluationsPage(page);
    } catch (loadError) {
      if (requestId === tableTopicsEvaluationsRequestId.current) {
        const message = loadError?.message || 'Unable to load Table Topics Evaluations.';
        setTableTopicsEvaluationsListError(message);
        if (!search.trim()) setTableTopicsEvaluationsError(message);
      }
    } finally {
      if (requestId === tableTopicsEvaluationsRequestId.current) {
        markSectionsLoaded(['tableTopicsEvaluations']);
        setTableTopicsEvaluationsLoadingMore(false);
      }
    }
  }, [authHeaders, canViewTableTopicsEvaluations, markSectionsLoaded, profile?._id, url]);

  useEffect(() => {
    loadTableTopicsEvaluations();
  }, [loadTableTopicsEvaluations]);

  useEffect(() => {
    if (!canViewTableTopicsEvaluations) return undefined;
    if (!showTableTopicsEvaluationsModal) return undefined;
    tableTopicsEvaluationsRequestId.current += 1;
    const timeout = setTimeout(() => {
      loadTableTopicsEvaluations({ page: 0, search: tableTopicsEvaluationsSearch });
    }, 300);
    return () => clearTimeout(timeout);
  }, [canViewTableTopicsEvaluations, loadTableTopicsEvaluations, showTableTopicsEvaluationsModal, tableTopicsEvaluationsSearch]);

  const loadOverviewData = useCallback(async () => {
    if (!profile?._id) return;

    setIsLoading(true);
    setError('');
    setSectionLoading({
      announcements: true,
      nextMeeting: true,
      nextAssignment: true,
      awards: true,
      tableTopicsEvaluations: true,
      feedback: true,
      evaluation: true,
      pathwayProjects: true,
    });

    try {
      const [nextMeetingResponse, nextAssignmentResponse, userPathwayResponse, config, meetingsConfigResponse, announcementsResponse] = await Promise.all([
        fetch(`${url}/meetings/comingup?includeAttendance=true`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return null;
          return res.json();
        }).catch(() => null),
        fetch(`${url}/roleNextAssignmentByUser/${profile._id}`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return null;
          return res.json();
        }).catch(() => null),
        fetch(`${url}/userpathway/${profile._id}`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data) ? data : [];
        }).catch(() => []),
        loadConfig().catch(() => ({})),
        fetch(`${url}/meetingsConfig`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return null;
          return res.json();
        }).catch(() => null),
        fetch(`${url}/announcements`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data) ? data : [];
        }).catch(() => []),
      ]);

      setNextMeeting(nextMeetingResponse);
      setNextAssignment(nextAssignmentResponse);
      setUserPathways(userPathwayResponse);
      setMeetingsConfig(meetingsConfigResponse);
      markSectionsLoaded(['nextMeeting', 'nextAssignment']);

      const profileAcl = normalizeText(profile?.acl || authUser?.user?.acl);
      const dismissedAnnouncementIds = new Set(readDismissedAnnouncementIds());

      const roleVisibleAnnouncements = announcementsResponse
        .filter((announcement) => {
          const announcementId = normalizeId(announcement?._id);

          if (announcement?.active === false) {
            if (announcementId) {
              restoreAnnouncementById(announcementId);
            }
            return false;
          }

          const audience = normalizeText(announcement?.to);
          const isVisibleForAcl = canAccessAudience(profileAcl, audience);

          if (!isVisibleForAcl) return false;
          if (announcementId && dismissedAnnouncementIds.has(announcementId)) return false;
          return true;
        })
        .sort((a, b) => {
          const dateA = toDate(a?.createdAt)?.getTime() || 0;
          const dateB = toDate(b?.createdAt)?.getTime() || 0;
          return dateB - dateA;
        });

      setOverviewAnnouncements(roleVisibleAnnouncements);
      setCollapsedOverviewAnnouncementIds(roleVisibleAnnouncements.map((announcement) => normalizeId(announcement?._id)).filter(Boolean));
      markSectionsLoaded(['announcements']);

      // Heavy data loads are intentionally deferred so cards can render immediately.
      (async () => {
        try {
          const awardsResponse = await fetch(`${url}/polls/awards/${profile._id}`, { headers: authHeaders }).then(async (res) => {
            if (!res.ok) return [];
            const data = await res.json();
            return Array.isArray(data) ? data : [];
          }).catch(() => []);
          setContestAwards(awardsResponse);
        } finally {
          markSectionsLoaded(['awards']);
        }
      })();

      const pathwaysUrl = config?.PATHWAYS_URL;
      (async () => {
        try {
          const pathwaysResponse = pathwaysUrl
            ? await fetch(`${pathwaysUrl}/pathways`).then(async (res) => {
              if (!res.ok) return [];
              const data = await res.json();
              return Array.isArray(data) ? data : [];
            }).catch(() => [])
            : [];
          setAllPathways(pathwaysResponse);

          const projectPairs = await Promise.all(
            userPathwayResponse.map(async (userPathway) => {
              try {
                const response = await fetch(
                  `${url}/user/${profile._id}/userPathway/${userPathway._id}/projects`,
                  { headers: authHeaders }
                );
                if (!response.ok) {
                  return [userPathway._id, []];
                }
                const projects = await response.json();
                return [userPathway._id, Array.isArray(projects) ? projects : []];
              } catch {
                return [userPathway._id, []];
              }
            })
          );

          const projectSnapshot = Object.fromEntries(projectPairs);
          setProjectsByPathway(projectSnapshot);
          setFeedbackProjectsByPathway(projectSnapshot);
          setEvaluationProjectsByPathway(projectSnapshot);
          setSuggestionProjectsByPathway(projectSnapshot);
        } finally {
          markSectionsLoaded(['feedback', 'evaluation', 'pathwayProjects']);
        }
      })();
    } catch (e) {
      setError(e?.message || 'Failed to load overview information.');
      markSectionsLoaded(['announcements', 'nextMeeting', 'nextAssignment', 'awards', 'tableTopicsEvaluations', 'feedback', 'evaluation', 'pathwayProjects']);
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, authUser?.user?.acl, markSectionsLoaded, profile?._id, profile?.acl, url]);

  useEffect(() => {
    loadOverviewData();
  }, [loadOverviewData]);

  const reloadOverviewSection = useCallback(async (section) => {
    if (!profile?._id) return;

    const pathwaySections = ['feedback', 'evaluation', 'pathwayProjects'];
    const loadingSections = [section];
    setSectionLoading((current) => ({
      ...current,
      ...Object.fromEntries(loadingSections.map((name) => [name, true])),
    }));
    setError('');

    try {
      if (section === 'announcements') {
        const response = await fetch(`${url}/announcements`, { headers: authHeaders });
        const data = response.ok ? await response.json() : [];
        const profileAcl = normalizeText(profile?.acl || authUser?.user?.acl);
        const dismissedAnnouncementIds = new Set(readDismissedAnnouncementIds());
        const announcements = (Array.isArray(data) ? data : [])
          .filter((announcement) => {
            const announcementId = normalizeId(announcement?._id);
            if (announcement?.active === false) {
              if (announcementId) restoreAnnouncementById(announcementId);
              return false;
            }
            if (!canAccessAudience(profileAcl, normalizeText(announcement?.to))) return false;
            return !announcementId || !dismissedAnnouncementIds.has(announcementId);
          })
          .sort((left, right) => (
            (toDate(right?.createdAt)?.getTime() || 0) - (toDate(left?.createdAt)?.getTime() || 0)
          ));
        setOverviewAnnouncements(announcements);
        setCollapsedOverviewAnnouncementIds(announcements.map((announcement) => normalizeId(announcement?._id)).filter(Boolean));
        return;
      }

      if (section === 'nextMeeting') {
        const [meeting, config] = await Promise.all([
          fetch(`${url}/meetings/comingup?includeAttendance=true`, { headers: authHeaders }).then((response) => (
            response.ok ? response.json() : null
          )),
          fetch(`${url}/meetingsConfig`, { headers: authHeaders }).then((response) => (
            response.ok ? response.json() : null
          )),
        ]);
        setNextMeeting(meeting);
        setMeetingsConfig(config);
        return;
      }

      if (section === 'nextAssignment') {
        const response = await fetch(`${url}/roleNextAssignmentByUser/${profile._id}`, { headers: authHeaders });
        setNextAssignment(response.ok ? await response.json() : null);
        return;
      }

      if (section === 'awards') {
        const response = await fetch(`${url}/polls/awards/${profile._id}`, { headers: authHeaders });
        const data = response.ok ? await response.json() : [];
        setContestAwards(Array.isArray(data) ? data : []);
        return;
      }

      if (section === 'tableTopicsEvaluations') {
        await loadTableTopicsEvaluations();
        return;
      }

      if (pathwaySections.includes(section)) {
        const projectPairs = await Promise.all(userPathways.map(async (userPathway) => {
          const response = await fetch(
            `${url}/user/${profile._id}/userPathway/${userPathway._id}/projects`,
            { headers: authHeaders }
          );
          if (!response.ok) return [userPathway._id, []];
          const projects = await response.json();
          return [userPathway._id, Array.isArray(projects) ? projects : []];
        }));
        const projectSnapshot = Object.fromEntries(projectPairs);
        if (section === 'feedback') setFeedbackProjectsByPathway(projectSnapshot);
        if (section === 'evaluation') setEvaluationProjectsByPathway(projectSnapshot);
        if (section === 'pathwayProjects') setSuggestionProjectsByPathway(projectSnapshot);
      }
    } catch (reloadError) {
      setError(reloadError?.message || 'Failed to reload profile card.');
    } finally {
      markSectionsLoaded(loadingSections);
    }
  }, [authHeaders, authUser?.user?.acl, loadTableTopicsEvaluations, markSectionsLoaded, profile?._id, profile?.acl, url, userPathways]);

  const pathwayLookup = useMemo(() => {
    const lookup = {};
    allPathways.forEach((pathway) => {
      lookup[pathway?._id] = pathway;
    });
    return lookup;
  }, [allPathways]);

  const projectLookup = useMemo(() => {
    const lookup = {};
    allPathways.forEach((pathway) => {
      [1, 2, 3, 4, 5].forEach((levelNo) => {
        const level = pathway?.[`level${levelNo}`];
        const levelTitle = normalizeText(level?.title || `Level ${levelNo}`);
        const register = (project) => {
          if (!project?._id) return;
          lookup[project._id] = {
            title: normalizeText(project.title || project._id),
            description: normalizeText(project.description1 || project.description2 || ''),
            levelTitle,
            pathwayName: normalizeText(pathway?.name || ''),
          };
        };
        (Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : []).forEach(register);
        (Array.isArray(level?.electiveProjects) ? level.electiveProjects : []).forEach(register);
      });
    });
    return lookup;
  }, [allPathways]);

  const collectPathwayProjects = useCallback((projectSnapshot) => {
    const collected = [];
    userPathways.forEach((userPathway) => {
      const pathway = pathwayLookup[userPathway.pathwayId];
      const projects = projectSnapshot[userPathway._id] || [];
      projects.forEach((project) => {
        const projectMeta = projectLookup[project.projectId] || {};
        collected.push({
          userPathway,
          pathway,
          project,
          projectTitle: projectMeta.title || normalizeText(project.projectId),
          projectDescription: projectMeta.description || '',
          levelTitle: projectMeta.levelTitle || '',
          pathwayName: projectMeta.pathwayName || normalizeText(pathway?.name || ''),
          attempts: Array.isArray(project?.attempts) ? project.attempts : [],
        });
      });
    });
    return collected;
  }, [projectLookup, pathwayLookup, userPathways]);

  const feedbackPathwayProjects = useMemo(
    () => collectPathwayProjects(feedbackProjectsByPathway),
    [collectPathwayProjects, feedbackProjectsByPathway]
  );
  const evaluationPathwayProjects = useMemo(
    () => collectPathwayProjects(evaluationProjectsByPathway),
    [collectPathwayProjects, evaluationProjectsByPathway]
  );

  const getLevelCompletionStatus = useCallback((level, completedIds) => {
    const mandatory = Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : [];
    const elective = Array.isArray(level?.electiveProjects) ? level.electiveProjects : [];
    const minElectives = Number(level?.minElectives || 0);

    const mandatoryDone = mandatory.every((project) => completedIds.has(normalizeId(project?._id)));
    const electiveCompletedCount = elective.filter((project) => completedIds.has(normalizeId(project?._id))).length;
    const electiveGoal = minElectives > 0 ? minElectives : elective.length;
    const electiveDone = electiveGoal === 0 ? true : electiveCompletedCount >= electiveGoal;

    return {
      mandatoryDone,
      electiveCompletedCount,
      electiveGoal,
      electiveDone,
      isComplete: mandatoryDone && electiveDone,
    };
  }, []);

  const collectAttempts = useCallback((pathwayProjectEntries) => {
    const attempts = [];
    pathwayProjectEntries.forEach((entry) => {
      entry.attempts.forEach((attempt) => {
        attempts.push({
          ...entry,
          attempt,
        });
      });
    });
    return attempts;
  }, []);

  const feedbackAttempts = useMemo(
    () => collectAttempts(feedbackPathwayProjects),
    [collectAttempts, feedbackPathwayProjects]
  );
  const evaluationAttempts = useMemo(
    () => collectAttempts(evaluationPathwayProjects),
    [collectAttempts, evaluationPathwayProjects]
  );

  const latestFeedbackAttempt = useMemo(() => {
    const attemptsWithFeedback = feedbackAttempts
      .map((entry) => {
        const feedbacks = Array.isArray(entry?.attempt?.feedbacks) ? entry.attempt.feedbacks : [];
        const visibleFeedbacks = feedbacks
          .map((feedback) => ({
            ...feedback,
            text: getFeedbackText(feedback),
          }))
          .filter((feedback) => Boolean(feedback.text));

        if (visibleFeedbacks.length === 0) return null;

        return {
          ...entry,
          feedbacks: visibleFeedbacks,
          sortDate: toDate(entry?.attempt?.scheduledDate || entry?.attempt?.updatedAt || entry?.attempt?.createdAt || entry?.project?.updatedAt),
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0));

    return attemptsWithFeedback[0] || null;
  }, [feedbackAttempts]);

  const feedbackCards = useMemo(() => {
    if (!latestFeedbackAttempt) return [];

    return latestFeedbackAttempt.feedbacks.map((feedback) => ({
      ...latestFeedbackAttempt,
      feedback,
      text: feedback.text,
      sortDate: latestFeedbackAttempt.sortDate,
    }));
  }, [latestFeedbackAttempt]);

  const latestEvaluation = useMemo(() => {
    const attemptsWithEvaluations = evaluationAttempts
      .map((entry) => {
        const evaluations = Array.isArray(entry?.attempt?.evaluations) ? entry.attempt.evaluations : [];
        if (evaluations.length === 0) return null;

        const latestEntryEvaluation = evaluations
          .slice()
          .sort((a, b) => {
            const dateA = toDate(a?.submittedAt || a?.updatedAt || a?.createdAt);
            const dateB = toDate(b?.submittedAt || b?.updatedAt || b?.createdAt);
            return (dateB || 0) - (dateA || 0);
          })[0] || null;

        return {
          ...entry,
          evaluation: latestEntryEvaluation,
          sortDate: toDate(entry?.attempt?.scheduledDate || entry?.attempt?.updatedAt || entry?.attempt?.createdAt || entry?.project?.updatedAt),
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0));

    return attemptsWithEvaluations[0] || null;
  }, [evaluationAttempts]);

  const remainingPathwayProjects = useMemo(() => {
    const pathwaysWithContext = userPathways
      .filter((userPathway) => !isCompletedUserPathway(userPathway))
      .map((userPathway) => {
        const pathway = pathwayLookup[userPathway.pathwayId];
        if (!pathway) return null;
        if (/other/i.test(normalizeText(pathway.name))) return null;

        const userProjects = suggestionProjectsByPathway[userPathway._id] || [];
        const completedIds = new Set(
          userProjects
            .filter(p => p.completed || p.completedAt)
            .map(p => normalizeId(p.projectId))
            .filter(Boolean)
        );

        let lastActivityMs = 0;
        userProjects.forEach((project) => {
          const projectTimes = [
            toDate(project?.completedAt),
            toDate(project?.updatedAt),
            toDate(project?.createdAt),
          ].filter(Boolean).map(d => d.getTime());

          const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
          attempts.forEach((attempt) => {
            [
              toDate(attempt?.scheduledDate),
              toDate(attempt?.updatedAt),
              toDate(attempt?.createdAt),
            ].filter(Boolean).forEach((d) => projectTimes.push(d.getTime()));

            const feedbacks = Array.isArray(attempt?.feedbacks) ? attempt.feedbacks : [];
            feedbacks.forEach((feedback) => {
              [toDate(feedback?.updatedAt), toDate(feedback?.createdAt)]
                .filter(Boolean)
                .forEach((d) => projectTimes.push(d.getTime()));
            });

            const evaluations = Array.isArray(attempt?.evaluations) ? attempt.evaluations : [];
            evaluations.forEach((evaluation) => {
              [toDate(evaluation?.submittedAt), toDate(evaluation?.updatedAt), toDate(evaluation?.createdAt)]
                .filter(Boolean)
                .forEach((d) => projectTimes.push(d.getTime()));
            });
          });

          const localLatest = projectTimes.length > 0 ? Math.max(...projectTimes) : 0;
          if (localLatest > lastActivityMs) {
            lastActivityMs = localLatest;
          }
        });

        const fallbackPathwayTime = Math.max(
          toDate(userPathway?.updatedAt)?.getTime() || 0,
          toDate(userPathway?.startedAt)?.getTime() || 0,
          toDate(userPathway?.createdAt)?.getTime() || 0,
        );

        const completedProjectCount = userProjects.filter((project) => project?.completed || project?.completedAt).length;
        const attemptedProjectCount = userProjects.filter((project) => {
          const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
          return attempts.length > 0;
        }).length;
        const touchedProjectCount = userProjects.length;

        const hasProgress = userProjects.some((project) => {
          if (project?.completed || project?.completedAt) return true;
          const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
          return attempts.length > 0;
        });

        return {
          userPathway,
          pathway,
          userProjects,
          completedIds,
          hasProgress,
          completedProjectCount,
          attemptedProjectCount,
          touchedProjectCount,
          activityMs: Math.max(lastActivityMs, fallbackPathwayTime),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.completedProjectCount !== b.completedProjectCount) {
          return b.completedProjectCount - a.completedProjectCount;
        }
        if (a.attemptedProjectCount !== b.attemptedProjectCount) {
          return b.attemptedProjectCount - a.attemptedProjectCount;
        }
        if (a.touchedProjectCount !== b.touchedProjectCount) {
          return b.touchedProjectCount - a.touchedProjectCount;
        }
        if (a.hasProgress !== b.hasProgress) return a.hasProgress ? -1 : 1;
        if (a.activityMs !== b.activityMs) return b.activityMs - a.activityMs;
        return normalizeText(a.pathway?.name).localeCompare(normalizeText(b.pathway?.name));
      });

    const selected = pathwaysWithContext[0];
    if (!selected) return [];

    const { pathway, userPathway, userProjects, completedIds } = selected;

    const levelSnapshots = [1, 2, 3, 4, 5]
      .map((levelNo) => {
        const level = pathway[`level${levelNo}`];
        if (!level) return null;

        const mandatory = Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : [];
        const elective = Array.isArray(level?.electiveProjects) ? level.electiveProjects : [];
        if (mandatory.length === 0 && elective.length === 0) return null;

        const completion = getLevelCompletionStatus(level, completedIds);
        const levelTitle = normalizeText(level?.title || `Level ${levelNo}`);
        const mandatoryRemaining = mandatory.filter((p) => !completedIds.has(normalizeId(p?._id))).length;
        const electiveRemaining = elective.filter((p) => !completedIds.has(normalizeId(p?._id))).length;

        return {
          levelNo,
          levelTitle,
          level,
          mandatory,
          elective,
          completion,
          mandatoryRemaining,
          electiveRemaining,
        };
      })
      .filter(Boolean);

    if (levelSnapshots.length === 0) return [];

    const firstIncompleteLevel = levelSnapshots.find((snapshot) => !snapshot.completion.isComplete) || null;

    const targetProjects = firstIncompleteLevel
      ? [
        ...firstIncompleteLevel.mandatory.map((project) => ({
          ...project,
          suggestionMode: 'required-level',
          pType: 'mandatory',
          levelNo: firstIncompleteLevel.levelNo,
          levelTitle: firstIncompleteLevel.levelTitle,
          mandatoryRemaining: firstIncompleteLevel.mandatoryRemaining,
          electiveRemaining: firstIncompleteLevel.electiveRemaining,
          electiveTarget: firstIncompleteLevel.completion.electiveGoal,
        })),
        ...firstIncompleteLevel.elective.map((project) => ({
          ...project,
          suggestionMode: 'required-level',
          pType: 'elective',
          levelNo: firstIncompleteLevel.levelNo,
          levelTitle: firstIncompleteLevel.levelTitle,
          mandatoryRemaining: firstIncompleteLevel.mandatoryRemaining,
          electiveRemaining: firstIncompleteLevel.electiveRemaining,
          electiveTarget: firstIncompleteLevel.completion.electiveGoal,
        })),
      ].filter((project) => !completedIds.has(normalizeId(project?._id)))
      : levelSnapshots
        .flatMap((snapshot) => snapshot.elective
          .filter((project) => !completedIds.has(normalizeId(project?._id)))
          .map((project) => ({
            ...project,
            suggestionMode: 'optional-electives',
            pType: 'elective',
            levelNo: snapshot.levelNo,
            levelTitle: snapshot.levelTitle,
            mandatoryRemaining: snapshot.mandatoryRemaining,
            electiveRemaining: snapshot.electiveRemaining,
            electiveTarget: snapshot.completion.electiveGoal,
          })))
        .sort((a, b) => {
          if (a.levelNo !== b.levelNo) return a.levelNo - b.levelNo;
          return normalizeText(a.title).localeCompare(normalizeText(b.title));
        });

    const seenProjectIds = new Set();

    return targetProjects.reduce((acc, project) => {
      const projectId = normalizeId(project?._id);
      if (!projectId || seenProjectIds.has(projectId)) return acc;
      seenProjectIds.add(projectId);

      const userProject = userProjects.find((up) => normalizeId(up?.projectId) === projectId) || null;
      acc.push({
        userPathway,
        pathway,
        pathwayName: normalizeText(pathway.name || ''),
        levelNo: project.levelNo,
        levelTitle: project.levelTitle,
        projectId,
        projectTitle: normalizeText(project.title || projectId),
        projectDescription: normalizeText(
          [project.description1, project.description2].filter(Boolean).join('\n\n')
        ),
        green: project.green || null,
        amber: project.amber || null,
        red: project.red || null,
        total: project.total || null,
        manual: project.manual || null,
        pType: project.pType,
        suggestionMode: project.suggestionMode,
        mandatoryRemaining: project.mandatoryRemaining,
        electiveRemaining: project.electiveRemaining,
        electiveTarget: project.electiveTarget,
        project: userProject,
      });
      return acc;
    }, []);
  }, [getLevelCompletionStatus, userPathways, pathwayLookup, suggestionProjectsByPathway]);

  const awards = useMemo(() => {
    const contestLabelByType = {
      bestSpeaker: 'Best Speaker',
      bestEvaluator: 'Best Evaluator',
      bestTableTopics: 'Best Table Topics',
    };

    const pathwayLabelById = userPathways.reduce((acc, userPathway) => {
      const name = normalizeText(pathwayLookup[userPathway?.pathwayId]?.name || 'Pathway');
      acc[userPathway?._id] = name;
      return acc;
    }, {});

    const contestAchievements = contestAwards
      .map((award) => {
        const contestType = normalizeText(award?.contestType);
        const meetingDate = formatShortDate(award?.meetingDate);
        return {
          dateText: meetingDate,
          category: 'Meeting Contest',
          achievement: contestLabelByType[contestType] || normalizeText(award?.title || 'Contest Winner'),
          dateValue: toDate(award?.meetingDate)?.getTime() || 0,
        };
      })
      .filter((entry) => Number(entry.dateValue) > 0);

    const pathwayCompletionAchievements = userPathways
      .map((userPathway) => {
        const completedAt = toDate(userPathway?.completedAt);
        if (!completedAt) return null;
        const pathwayName = pathwayLabelById[userPathway?._id] || 'Pathway';
        return {
          dateText: formatShortDate(completedAt),
          category: 'Pathway Completion',
          achievement: `${pathwayName} completed`,
          dateValue: completedAt.getTime(),
        };
      })
      .filter(Boolean);

    const levelCompletionAchievements = userPathways
      .flatMap((userPathway) => {
        const pathway = pathwayLookup[userPathway?.pathwayId];
        if (!pathway) return [];

        const pathwayName = pathwayLabelById[userPathway?._id] || 'Pathway';
        const userProjects = Array.isArray(projectsByPathway[userPathway?._id]) ? projectsByPathway[userPathway._id] : [];
        const completedByProjectId = new Map(
          userProjects
            .map((project) => {
              const projectId = normalizeId(project?.projectId);
              const completedAt = toDate(project?.completedAt);
              return [projectId, completedAt];
            })
            .filter(([projectId, completedAt]) => Boolean(projectId) && Boolean(completedAt))
        );

        return [1, 2, 3, 4, 5]
          .map((levelNo) => {
            const level = pathway?.[`level${levelNo}`];
            if (!level) return null;

            const mandatory = Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : [];
            const electives = Array.isArray(level?.electiveProjects) ? level.electiveProjects : [];
            const minElectives = Number(level?.minElectives || 0);
            const electiveGoal = minElectives > 0 ? minElectives : electives.length;

            const mandatoryDates = mandatory
              .map((project) => completedByProjectId.get(normalizeId(project?._id)) || null)
              .filter(Boolean)
              .map((date) => date.getTime());

            if (mandatoryDates.length !== mandatory.length) {
              return null;
            }

            const electiveDates = electives
              .map((project) => completedByProjectId.get(normalizeId(project?._id)) || null)
              .filter(Boolean)
              .map((date) => date.getTime())
              .sort((a, b) => a - b);

            if (electiveDates.length < electiveGoal) {
              return null;
            }

            const mandatoryCompletionTime = mandatoryDates.length > 0 ? Math.max(...mandatoryDates) : 0;
            const electiveCompletionTime = electiveGoal > 0 ? electiveDates[electiveGoal - 1] : 0;
            const levelCompletionTime = Math.max(mandatoryCompletionTime, electiveCompletionTime);
            if (!levelCompletionTime) return null;

            return {
              dateText: formatShortDate(new Date(levelCompletionTime)),
              category: 'Pathway Level Completion',
              achievement: `${pathwayName} - ${normalizeText(level?.title || `Level ${levelNo}`)} completed`,
              dateValue: levelCompletionTime,
            };
          })
          .filter(Boolean);
      });

    const items = [
      ...contestAchievements,
      ...pathwayCompletionAchievements,
      ...levelCompletionAchievements,
    ];

    return items
      .filter((entry) => Number(entry?.dateValue) > 0)
      .map((entry) => ({
        ...entry,
        searchText: `${entry.dateText} ${entry.category} ${entry.achievement}`.toLowerCase(),
      }))
      .sort((a, b) => {
        if (a.dateValue === b.dateValue) {
          return a.achievement.localeCompare(b.achievement);
        }
        return b.dateValue - a.dateValue;
      });
  }, [contestAwards, pathwayLookup, projectsByPathway, userPathways]);

  const awardPreview = useMemo(() => awards.slice(0, 6), [awards]);

  const filteredAwards = useMemo(() => {
    const query = normalizeText(awardsSearch).toLowerCase();
    if (!query) return awards;
    return awards.filter((award) => award.searchText.includes(query));
  }, [awards, awardsSearch]);

  const nextAssignmentItems = useMemo(
    () => (Array.isArray(nextAssignment?.roleAssignments) ? nextAssignment.roleAssignments : []),
    [nextAssignment?.roleAssignments]
  );

  useEffect(() => {
    if (nextAssignmentItems.length === 0) {
      setActiveAssignmentIndex(0);
      return;
    }
    setActiveAssignmentIndex((prev) => Math.min(prev, nextAssignmentItems.length - 1));
  }, [nextAssignmentItems.length]);

  useEffect(() => {
    if (feedbackCards.length === 0) {
      setActiveFeedbackIndex(0);
      return;
    }
    setActiveFeedbackIndex((prev) => Math.min(prev, feedbackCards.length - 1));
  }, [feedbackCards.length]);

  const activeAssignment = nextAssignmentItems[activeAssignmentIndex] || null;
  const activeFeedback = feedbackCards[activeFeedbackIndex] || null;
  const hasActiveAssignment = Boolean(activeAssignment?._id);
  const nextAssignmentEmptyMessage = normalizeText(
    nextAssignment?.message || 'No upcoming role assignment found'
  );

  const nextAssignmentStatusClass = activeAssignment?.confirmed === true
    ? 'confirmed'
    : activeAssignment?.confirmed === false
      ? 'rejected'
      : 'waiting';

  const nextAssignmentStatus = nextAssignmentStatusClass === 'confirmed'
    ? 'Confirmed'
    : nextAssignmentStatusClass === 'rejected'
      ? 'Rejected'
      : 'Awaiting your response';

  const toastmasterAssignment = useMemo(() => {
    const assignments = Array.isArray(nextMeeting?.roleAssignments) ? nextMeeting.roleAssignments : [];
    return assignments.find((assignment) => normalizeText(assignment?.role?.name).toLowerCase() === 'toastmaster') || null;
  }, [nextMeeting?.roleAssignments]);

  const toastmasterName = normalizeText(
    toastmasterAssignment?.user?.firstName
      ? `${toastmasterAssignment.user.firstName} ${toastmasterAssignment?.user?.lastName || ''}`
      : 'TBA'
  );
  const toastmasterAvatar = resolveAvatarSrc(toastmasterAssignment?.user?.avatar);
  const toastmasterInitials = getUserInitials(toastmasterAssignment?.user, 'T');

  const canToggleAttendance = Boolean(
    nextMeeting?._id && profileUserId && authUser?.user && (
      authUser.user.acl === 'Officer' || authUserId === profileUserId
    )
  );

  const canManageNextAssignment = Boolean(
    activeAssignment?._id && authUser?.user && (
      String(authUser.user._id || authUser.user.id || '') === profileUserId
      || (Array.isArray(authUser.user.clubRoles)
        && (authUser.user.clubRoles.includes('President') || authUser.user.clubRoles.includes('Vice President of Education')))
    )
  );

  const isProfileAttending = useMemo(() => {
    const attendance = Array.isArray(nextMeeting?.attendance) ? nextMeeting.attendance : [];
    return attendance.some((entry) => {
      if (typeof entry === 'string') return entry === profileUserId;
      if (entry && typeof entry === 'object') {
        const value = entry._id || entry.id;
        return String(value || '') === profileUserId;
      }
      return false;
    });
  }, [nextMeeting?.attendance, profileUserId]);

  const hasProfileDeclinedAttendance = useMemo(() => {
    const declinedAttendance = Array.isArray(nextMeeting?.declinedAttendance) ? nextMeeting.declinedAttendance : [];
    return declinedAttendance.some((entry) => normalizeId(entry) === profileUserId);
  }, [nextMeeting?.declinedAttendance, profileUserId]);

  const attendanceStatusClass = isProfileAttending
    ? 'confirmed'
    : hasProfileDeclinedAttendance
      ? 'rejected'
      : 'waiting';
  const attendanceStatus = isProfileAttending
    ? 'Attending'
    : hasProfileDeclinedAttendance
      ? 'Not attending'
      : 'Awaiting your response';

  const isOnlineMeeting = nextMeeting?.meetingMode === 'online';
  const locationLabel = isOnlineMeeting
    ? normalizeText(nextMeeting?.meetingOnlineLink || 'Online meeting')
    : normalizeText(nextMeeting?.meetingAddress || 'TBA');
  const mapLink = !isOnlineMeeting
    ? normalizeText(nextMeeting?.inPersonMapsLink || meetingsConfig?.inPersonMapsLink || '')
    : '';

  const handleAttendanceStatusChange = async (attending) => {
    if (!canToggleAttendance || attendanceBusy) return;
    setAttendanceBusy(true);

    try {
      const response = await fetch(`${url}/meeting/${nextMeeting._id}/attendance/${profileUserId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ attending }),
      });

      if (!response.ok) {
        throw new Error('Failed to update attendance.');
      }

      const payload = await response.json();
      setNextMeeting((prev) => {
        const updatedUserId = String(payload?.userId || profileUserId);
        const currentAttendance = Array.isArray(prev?.attendance) ? prev.attendance : [];
        const currentDeclinedAttendance = Array.isArray(prev?.declinedAttendance) ? prev.declinedAttendance : [];
        const attendanceWithoutUser = currentAttendance.filter((entry) => normalizeId(entry) !== updatedUserId);
        const declinedAttendanceWithoutUser = currentDeclinedAttendance.filter((entry) => normalizeId(entry) !== updatedUserId);

        return {
          ...prev,
          attendance: payload?.attending === true
            ? [...attendanceWithoutUser, updatedUserId]
            : attendanceWithoutUser,
          declinedAttendance: payload?.attending === false
            ? [...declinedAttendanceWithoutUser, updatedUserId]
            : declinedAttendanceWithoutUser,
        };
      });
    } catch (e) {
      setError(e?.message || 'Failed to update attendance.');
    } finally {
      setAttendanceBusy(false);
    }
  };

  const handleAssignmentStatusChange = async (confirmedValue) => {
    if (!activeAssignment?._id || assignmentBusy || !canManageNextAssignment) return;

    setAssignmentBusy(true);

    try {
      const response = await fetch(`${url}/roleAssignment/${activeAssignment._id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ confirmed: confirmedValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update assignment status.');
      }

      setNextAssignment((prev) => {
        const nextRoleAssignments = Array.isArray(prev?.roleAssignments)
          ? prev.roleAssignments.map((entry) => (
            String(entry?._id || '') === String(activeAssignment._id)
              ? { ...entry, confirmed: confirmedValue }
              : entry
          ))
          : prev?.roleAssignments;

        return {
          ...prev,
          roleAssignments: nextRoleAssignments,
        };
      });
    } catch (e) {
      setError(e?.message || 'Failed to update assignment status.');
    } finally {
      setAssignmentBusy(false);
    }
  };

  useEffect(() => {
    if (remainingPathwayProjects.length === 0) {
      setActivePathwayProjectIndex(0);
      return;
    }
    setActivePathwayProjectIndex((prev) => Math.min(prev, remainingPathwayProjects.length - 1));
  }, [remainingPathwayProjects.length]);

  const activePathwayProject = remainingPathwayProjects[activePathwayProjectIndex] || null;

  const showAssignmentPager = nextAssignmentItems.length > 1;
  const showFeedbackPager = feedbackCards.length > 1;
  const showPathwayPager = remainingPathwayProjects.length > 1;
  const nextAssignmentTitle = nextAssignmentItems.length > 1 ? 'Next Assignments' : 'Next Assignment';
  const feedbackTitle = feedbackCards.length > 1 ? 'Feedbacks' : 'Feedback';
  const pathwayProjectTitle = remainingPathwayProjects.length > 1 ? 'Next Pathway Projects' : 'Next Pathway Project';
  const supportsPathwaysTab = isPathwaysEligible(profile?.acl);
  const canViewPersonalProgressCards = Boolean(
    profileUserId
    && authUser?.user
    && (profileUserId === authUserId || authUser.user.acl === 'Officer')
    && supportsPathwaysTab
  );

  const goToPathwaySelection = () => {
    if (!profileUserId) return;
    navigate(`/profile/${profileUserId}`, {
      state: {
        profileTab: supportsPathwaysTab ? 'pathways' : 'mypathways',
        source: 'overview-empty-pathway',
        at: Date.now(),
      },
    });
  };

  const goToPathwayProject = (entry, source) => {
    if (!profileUserId || !entry?.userPathway?._id || !entry?.project?.projectId) return;

    navigate(`/profile/${profileUserId}`, {
      state: {
        profileTab: 'mypathways',
        pathwayProjectTarget: {
          userPathwayId: entry.userPathway._id,
          pathwayId: entry.userPathway.pathwayId,
          projectId: entry.project.projectId,
          attemptId: entry.attempt?._id || '',
          source,
          at: Date.now(),
        },
        source,
        at: Date.now(),
      },
    });
  };

  const goPrevAssignment = () => {
    if (!showAssignmentPager) return;
    setActiveAssignmentIndex((prev) => (prev === 0 ? nextAssignmentItems.length - 1 : prev - 1));
  };

  const goNextAssignment = () => {
    if (!showAssignmentPager) return;
    setActiveAssignmentIndex((prev) => (prev + 1) % nextAssignmentItems.length);
  };

  const goPrevFeedback = () => {
    if (!showFeedbackPager) return;
    setActiveFeedbackIndex((prev) => (prev === 0 ? feedbackCards.length - 1 : prev - 1));
  };

  const goNextFeedback = () => {
    if (!showFeedbackPager) return;
    setActiveFeedbackIndex((prev) => (prev + 1) % feedbackCards.length);
  };

  const goPrevPathwayProject = () => {
    if (!showPathwayPager) return;
    setActivePathwayProjectIndex((prev) => (prev === 0 ? remainingPathwayProjects.length - 1 : prev - 1));
  };

  const goNextPathwayProject = () => {
    if (!showPathwayPager) return;
    setActivePathwayProjectIndex((prev) => (prev + 1) % remainingPathwayProjects.length);
  };

  const pathwayProjectModalBody = activePathwayProject ? (
    <div style={{ maxHeight: '72vh', overflowY: 'auto' }}>
      {(() => {
        const manual = activePathwayProject?.manual || {};
        const manualOverview = normalizeText(manual?.overview || '');
        const manualReadingLevel = normalizeText(manual?.readingLevel || 'B1').toUpperCase();
        const validReadingLevels = ['A1', 'A2', 'B1', 'B2'];
        const readingLevel = validReadingLevels.includes(manualReadingLevel) ? manualReadingLevel : 'B1';
        const objectives = sanitizeManualList(manual?.objectives);
        const steps = sanitizeManualList(manual?.steps);
        const tips = sanitizeManualList(manual?.tips);
        const reflection = sanitizeManualList(manual?.reflection);
        const hasManualContent = Boolean(
          manualOverview || objectives.length || steps.length || tips.length || reflection.length
        );

        return (
          <>
            <p className="mb-1 profile-overview-meta">{activePathwayProject.pathwayName}</p>
            <p className="mb-1">
              <strong>Level {activePathwayProject.levelNo}</strong>
              {activePathwayProject.levelTitle ? ` – ${activePathwayProject.levelTitle}` : ''}
            </p>
            {activePathwayProject?.suggestionMode === 'optional-electives' && (
              <p className="mb-2 profile-overview-meta" style={{ fontSize: '0.78rem' }}>
                Optional electives remaining
              </p>
            )}
            <p className="mb-3 profile-overview-meta" style={{ fontSize: '0.86rem' }}>
              Mandatory: {activePathwayProject.mandatoryRemaining} remaining &nbsp;·&nbsp; Elective: {activePathwayProject.electiveRemaining} remaining
            </p>
            {(activePathwayProject.green || activePathwayProject.amber || activePathwayProject.red) && (
              <div className="profile-overview-timers mb-3">
                {activePathwayProject.green && <span className="profile-overview-timer profile-overview-timer--green">{activePathwayProject.green}</span>}
                {activePathwayProject.amber && <span className="profile-overview-timer profile-overview-timer--amber">{activePathwayProject.amber}</span>}
                {activePathwayProject.red && <span className="profile-overview-timer profile-overview-timer--red">{activePathwayProject.red}</span>}
              </div>
            )}
            {activePathwayProject.projectDescription && (
              <p className="mb-3">{activePathwayProject.projectDescription}</p>
            )}

            <hr style={{ opacity: 0.2 }} />
            <div className="project-manual-header mb-3">
              <div>
                <div className="project-manual-kicker">Speaker Manual</div>
                <h6 className="project-manual-title mb-0">{activePathwayProject.projectTitle}</h6>
              </div>
              <span className={`project-manual-reading-level project-manual-reading-level--${readingLevel.toLowerCase()}`}>
                Reading level {readingLevel}
              </span>
            </div>

            {hasManualContent ? (
              <div className="project-manual-sections">
                {manualOverview && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Overview</h6>
                    <p className="project-manual-overview mb-0">{manualOverview}</p>
                  </section>
                )}

                {objectives.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Objectives</h6>
                    <ul className="project-manual-list">
                      {objectives.map((item, index) => (
                        <li key={`manual-objective-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {steps.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Steps</h6>
                    <ol className="project-manual-list project-manual-list--ordered">
                      {steps.map((item, index) => (
                        <li key={`manual-step-${index}`}>{item}</li>
                      ))}
                    </ol>
                  </section>
                )}

                {tips.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Tips</h6>
                    <ul className="project-manual-list">
                      {tips.map((item, index) => (
                        <li key={`manual-tip-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {reflection.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Reflection</h6>
                    <ul className="project-manual-list">
                      {reflection.map((item, index) => (
                        <li key={`manual-reflection-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            ) : (
              <div className="project-content-placeholder">
                Manual content is not available for this project yet.
              </div>
            )}
          </>
        );
      })()}
    </div>
  ) : null;

  const pathwayProjectModalFooter = (
    <button type="button" className="profile-overview-button" onClick={() => setShowProjectModal(false)}>Close</button>
  );

  const awardsModalBody = (
    <div style={{ maxHeight: '72vh', overflowY: 'auto' }}>
      <div className="assignments-history-top">
        <h4>Awards History</h4>
        <span className="profile-overview-meta">{filteredAwards.length} entr{filteredAwards.length === 1 ? 'y' : 'ies'}</span>
      </div>
      <div className="profile-overview-divider" />

      <div className="assignments-search-wrap mb-3">
        <input
          type="text"
          className="assignments-search-input"
          placeholder="Search date, category, award..."
          value={awardsSearch}
          onChange={(event) => setAwardsSearch(event.target.value)}
        />
        {awardsSearch && (
          <button
            type="button"
            className="assignments-search-clear"
            onClick={() => setAwardsSearch('')}
            aria-label="Clear search"
          >
            <i className="bi bi-x" />
          </button>
        )}
      </div>

      <div className="assignments-history-table-wrap">
        <div className="assignments-history-table assignments-history-table--head awards-history-table">
          <div>Date</div>
          <div>Category</div>
          <div>Award</div>
        </div>

        {filteredAwards.length > 0 ? (
          filteredAwards.map((award, index) => (
            <div key={`award-row-${index}`} className="assignments-history-table assignments-history-table--row awards-history-table">
              <div className="assignments-history-cell" data-label="Date">
                <span>{award.dateText}</span>
              </div>

              <div className="assignments-history-cell" data-label="Category">
                <span>{award.category}</span>
              </div>

              <div className="assignments-history-cell" data-label="Award">
                <span>{award.achievement}</span>
              </div>
            </div>
          ))
        ) : (
          <p className="mb-0 profile-overview-empty-message">No awards match your search.</p>
        )}
      </div>
    </div>
  );

  const awardsModalFooter = (
    <button type="button" className="profile-overview-button" onClick={() => setShowAwardsModal(false)}>Close</button>
  );

  const announcementModalBody = selectedOverviewAnnouncement ? (
    <div className="profile-overview-announcement-modal-wrap">
      <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card profile-overview-announcement-modal-card">
        <Card.Body>
          <div className="profile-overview-card-top">
            <h4 className="profile-overview-announcement-title">
              <i className="bi bi-megaphone-fill" aria-hidden="true" />
              <span>{normalizeText(selectedOverviewAnnouncement?.title || 'Announcement')}</span>
            </h4>
            <span className="profile-overview-badge profile-overview-announcement-audience">
              {normalizeText(selectedOverviewAnnouncement?.to || 'Public')}
            </span>
          </div>
          <div className="profile-overview-divider" />

          {selectedOverviewAnnouncement?.shortDescription && (
            <div
              className="profile-overview-announcement-short mb-3"
              dangerouslySetInnerHTML={{ __html: selectedOverviewAnnouncement.shortDescription }}
            ></div>
          )}

          <div
            className="profile-overview-announcement-full"
            dangerouslySetInnerHTML={{ __html: selectedOverviewAnnouncement?.description || '' }}
          ></div>
        </Card.Body>
      </Card>
    </div>
  ) : null;

  const announcementModalFooter = (
    <button type="button" className="profile-overview-button" onClick={() => setShowAnnouncementModal(false)}>Close</button>
  );

  return (
    <div className="profile-overview">
      {isLoading && <p className="profile-overview-meta mb-2">Loading overview...</p>}
      {error && <p className="text-danger mb-3">{error}</p>}

      {(sectionLoading.announcements || overviewAnnouncements.length > 0) && (
        <Row className="g-3 mb-1 profile-overview-grid-row">
          <Col xs={12}>
            <div className="profile-overview-announcement-list">
              {sectionLoading.announcements ? (
                <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card profile-overview-announcement-card">
                  <Card.Body>
                    <div className="profile-overview-card-loader">
                      <span className="profile-overview-loader-line profile-overview-loader-line--title"></span>
                      <span className="profile-overview-loader-line profile-overview-loader-line--w90"></span>
                      <span className="profile-overview-loader-line profile-overview-loader-line--w70"></span>
                    </div>
                  </Card.Body>
                </Card>
              ) : overviewAnnouncements.map((announcement) => (
                <Card key={announcement._id} className="profile-overview-card meetings-mock-card-shell meetings-page-card profile-overview-announcement-card">
                  <Card.Header className="profile-overview-announcement-header">
                    <button
                      type="button"
                      className="profile-overview-announcement-header-button"
                      aria-expanded={!collapsedOverviewAnnouncementIds.includes(normalizeId(announcement?._id))}
                      onClick={() => toggleOverviewAnnouncementCollapse(announcement?._id)}
                    >
                      <span className="profile-overview-card-top profile-overview-announcement-header-main">
                        <h4 className="profile-overview-announcement-title mb-0">
                          <i className="bi bi-megaphone-fill" aria-hidden="true" />
                          <span>{normalizeText(announcement?.title || 'Announcement')}</span>
                        </h4>
                        <span className="profile-overview-announcement-header-actions">
                          <span className="profile-overview-badge profile-overview-announcement-audience">
                            {normalizeText(announcement?.to || 'Public')}
                          </span>
                          <i className={`bi ${collapsedOverviewAnnouncementIds.includes(normalizeId(announcement?._id)) ? 'bi-chevron-down' : 'bi-chevron-up'}`} aria-hidden="true" />
                        </span>
                      </span>
                    </button>
                    <CardReloadButton
                      loading={sectionLoading.announcements}
                      onClick={() => reloadOverviewSection('announcements')}
                      label="announcements"
                    />
                  </Card.Header>
                  <Collapse in={!collapsedOverviewAnnouncementIds.includes(normalizeId(announcement?._id))}>
                    <div>
                      <Card.Body>
                        {announcement?.shortDescription ? (
                          <div
                            className="profile-overview-announcement-short"
                            dangerouslySetInnerHTML={{ __html: announcement.shortDescription }}
                          ></div>
                        ) : (
                          <p className="mb-0 profile-overview-meta">A new announcement is available.</p>
                        )}
                        <div className="profile-overview-card-link-wrap mt-3 profile-overview-announcement-actions">
                          <button
                            type="button"
                            className="profile-overview-button"
                            onClick={() => {
                              setSelectedOverviewAnnouncement(announcement);
                              setShowAnnouncementModal(true);
                            }}
                          >
                            Read more <i className="bi bi-arrow-right" />
                          </button>
                          <button
                            type="button"
                            className="profile-overview-announcement-dismiss-btn"
                            onClick={() => handleDismissOverviewAnnouncement(announcement?._id)}
                          >
                            Remove announcement
                          </button>
                        </div>
                      </Card.Body>
                    </div>
                  </Collapse>
                </Card>
              ))}
            </div>
          </Col>
        </Row>
      )}

      <Row className="g-3 profile-overview-grid-row">
        <Col xs={12} lg={4}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <div className="profile-overview-card-top">
                <h4>Next Meeting</h4>
                <div className="profile-overview-card-header-actions">
                  {!sectionLoading.nextMeeting && nextMeeting?.meetingDate && (
                    <span className="profile-overview-badge">{daysLeftLabel(nextMeeting.meetingDate, nextMeeting.meetingTime, now)}</span>
                  )}
                  <CardReloadButton
                    loading={sectionLoading.nextMeeting}
                    onClick={() => reloadOverviewSection('nextMeeting')}
                    label="next meeting"
                  />
                </div>
              </div>
              <div className="profile-overview-divider" />
              {sectionLoading.nextMeeting ? (
                <div className="profile-overview-card-loader">
                  <span className="profile-overview-loader-line profile-overview-loader-line--w90"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w80"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w65"></span>
                </div>
              ) : (
                <>
                  <ul className="profile-overview-list mb-2">
                    <li><i className="bi bi-calendar3" /> {formatLongDate(nextMeeting?.meetingDate)} - {normalizeText(nextMeeting?.meetingTime || 'TBA')}</li>
                    <li><i className="bi bi-bookmark-check-fill" /> Theme: {normalizeText(nextMeeting?.meetingTheme || 'To Be Announced')}</li>
                    <li>
                      {toastmasterAvatar
                        ? <img src={toastmasterAvatar} alt="Toastmaster avatar" className="profile-overview-inline-avatar" />
                        : <span className="profile-overview-inline-avatar profile-overview-inline-avatar--initials">{toastmasterInitials}</span>}
                      Toastmaster: {toastmasterName}
                    </li>
                    <li>
                      <i className={isOnlineMeeting ? 'bi bi-camera-video-fill' : 'bi bi-geo-alt-fill'} />
                      Location: {locationLabel}
                    </li>
                    {!isOnlineMeeting && mapLink && (
                      <li className="profile-overview-sub-row">
                        <span className="profile-overview-sub-row-indent" aria-hidden="true"></span>
                        <a href={mapLink} className="profile-overview-card-link" target="_blank" rel="noreferrer">Open map</a>
                      </li>
                    )}
                  </ul>
                  {canToggleAttendance && (
                    <>
                      <div className="profile-overview-status-wrap mb-2">
                        <span className={`profile-overview-status-badge profile-overview-status-badge--${attendanceStatusClass}`}>
                          {attendanceStatus}
                        </span>
                      </div>
                      <div className="profile-overview-assignment-actions mb-2">
                        <button
                          type="button"
                          className="profile-overview-assignment-btn profile-overview-assignment-btn--success"
                          onClick={() => handleAttendanceStatusChange(true)}
                          disabled={attendanceBusy}
                        >
                          I'll attend
                        </button>
                        <button
                          type="button"
                          className="profile-overview-assignment-btn profile-overview-assignment-btn--danger"
                          onClick={() => handleAttendanceStatusChange(false)}
                          disabled={attendanceBusy}
                        >
                          I will not attend
                        </button>
                        <button
                          type="button"
                          className="profile-overview-assignment-btn profile-overview-assignment-btn--neutral"
                          onClick={() => handleAttendanceStatusChange(null)}
                          disabled={attendanceBusy}
                        >
                          Reset
                        </button>
                      </div>
                    </>
                  )}
                  {nextMeeting?._id && (
                    <div className="profile-overview-card-link-wrap">
                      <Link to={`/meeting/${nextMeeting._id}`} className="profile-overview-card-link">
                        View full meeting agenda <i className="bi bi-chevron-right" />
                      </Link>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <div className="profile-overview-card-top">
                <h4>{nextAssignmentTitle}</h4>
                <CardReloadButton
                  loading={sectionLoading.nextAssignment}
                  onClick={() => reloadOverviewSection('nextAssignment')}
                  label="next assignment"
                />
              </div>
              <div className="profile-overview-divider" />
              {sectionLoading.nextAssignment ? (
                <div className="profile-overview-card-loader">
                  <span className="profile-overview-loader-line profile-overview-loader-line--w85"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w60"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w70"></span>
                </div>
              ) : (
                <>
                  {showAssignmentPager && (
                    <div className="profile-overview-assignment-nav mb-2">
                      <button
                        type="button"
                        className="profile-overview-assignment-nav-btn"
                        onClick={goPrevAssignment}
                        aria-label="Previous assignment"
                      >
                        <i className="bi bi-chevron-left" />
                      </button>
                      <span className="profile-overview-assignment-nav-count">
                        {activeAssignmentIndex + 1} / {nextAssignmentItems.length}
                      </span>
                      <button
                        type="button"
                        className="profile-overview-assignment-nav-btn"
                        onClick={goNextAssignment}
                        aria-label="Next assignment"
                      >
                        <i className="bi bi-chevron-right" />
                      </button>
                    </div>
                  )}
                  {hasActiveAssignment ? (
                    <>
                      <p className="mb-1"><i className="bi bi-calendar3" /> {formatShortDate(nextAssignment?.meetingDate)}</p>
                      <p className="mb-2"><i className="bi bi-bookmark-check-fill" /> {normalizeText(nextAssignment?.meetingTheme || 'Theme not set')}</p>
                      <h3 className="profile-overview-main-line mb-3">{normalizeText(activeAssignment?.role?.name)}</h3>
                    </>
                  ) : (
                    <h3 className="profile-overview-main-line mb-3">{nextAssignmentEmptyMessage}</h3>
                  )}
                  {hasActiveAssignment && (
                    <div className="profile-overview-status-wrap">
                      <span className={`profile-overview-status-badge profile-overview-status-badge--${nextAssignmentStatusClass}`}>
                        {nextAssignmentStatus}
                      </span>
                    </div>
                  )}
                  {canManageNextAssignment && (
                    <div className="profile-overview-assignment-actions mt-2 mb-2">
                      <button
                        type="button"
                        className="profile-overview-assignment-btn profile-overview-assignment-btn--danger"
                        onClick={() => handleAssignmentStatusChange(false)}
                        disabled={assignmentBusy}
                      >
                        Decline
                      </button>
                      <button
                        type="button"
                        className="profile-overview-assignment-btn profile-overview-assignment-btn--success"
                        onClick={() => handleAssignmentStatusChange(true)}
                        disabled={assignmentBusy}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="profile-overview-assignment-btn profile-overview-assignment-btn--neutral"
                        onClick={() => handleAssignmentStatusChange(null)}
                        disabled={assignmentBusy}
                      >
                        Reset
                      </button>
                    </div>
                  )}
                  {nextAssignment?._id && (
                    <div className="profile-overview-card-link-wrap mt-2">
                      <Link to={`/meeting/${nextAssignment._id}`} className="profile-overview-card-link">
                        View meeting agenda <i className="bi bi-chevron-right" />
                      </Link>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <div className="profile-overview-card-top">
                <h4 className="mb-0">Awards</h4>
                <div className="profile-overview-card-header-actions">
                  <button
                    type="button"
                    className="profile-overview-button"
                    onClick={() => setShowAwardsModal(true)}
                    style={{ fontSize: '0.82rem', padding: '0.2rem 0.42rem' }}
                    disabled={sectionLoading.awards}
                  >
                    View all <i className="bi bi-chevron-right" />
                  </button>
                  <CardReloadButton
                    loading={sectionLoading.awards}
                    onClick={() => reloadOverviewSection('awards')}
                    label="awards"
                  />
                </div>
              </div>
              <div className="profile-overview-divider" />
              {sectionLoading.awards ? (
                <div className="profile-overview-card-loader">
                  <span className="profile-overview-loader-line profile-overview-loader-line--w85"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w88"></span>
                  <span className="profile-overview-loader-line profile-overview-loader-line--w72"></span>
                </div>
              ) : (
                <>
                  <ul className="profile-overview-awards profile-overview-awards-scroll mb-0">
                    {(awardPreview.length > 0
                      ? awardPreview.map((award) => `${award.dateText} - ${award.achievement}`)
                      : ['No achievements yet.']).map((award, idx) => (
                        <li key={`award-${idx}`}><i className="bi bi-award-fill" /> {award}</li>
                      ))}
                  </ul>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {(canViewPersonalProgressCards || canViewTableTopicsEvaluations) && (
        <Row className="g-3 mt-1 profile-overview-grid-row">
          {canViewTableTopicsEvaluations && (
          <Col xs={12} lg={4}>
            <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="profile-overview-card-top">
                  <h4 className="mb-0">Table Topics Evaluations</h4>
                  <div className="profile-overview-card-header-actions">
                    {!sectionLoading.tableTopicsEvaluations && tableTopicsEvaluationPreview && (
                      <button
                        type="button"
                        className="profile-overview-button"
                        onClick={() => {
                          setTableTopicsEvaluationsSearch('');
                          setTableTopicsEvaluations([]);
                          setTableTopicsEvaluationsHasMore(false);
                          setTableTopicsEvaluationsPage(0);
                          setSectionLoading(current => ({ ...current, tableTopicsEvaluations: true }));
                          setShowTableTopicsEvaluationsModal(true);
                        }}
                        style={{ fontSize: '0.82rem', padding: '0.2rem 0.42rem' }}
                      >
                        View all <i className="bi bi-chevron-right" />
                      </button>
                    )}
                    <CardReloadButton
                      loading={sectionLoading.tableTopicsEvaluations}
                      onClick={loadTableTopicsEvaluations}
                      label="Table Topics Evaluations"
                    />
                  </div>
                </div>
                <div className="profile-overview-divider" />
                {sectionLoading.tableTopicsEvaluations ? (
                  <div className="profile-overview-card-loader">
                    <span className="profile-overview-loader-line profile-overview-loader-line--w86"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w82"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w95"></span>
                  </div>
                ) : tableTopicsEvaluationsError ? (
                  <p className="mb-0 profile-overview-empty-message">{tableTopicsEvaluationsError}</p>
                ) : tableTopicsEvaluationPreview ? (
                  <TableTopicsEvaluationDetails evaluation={tableTopicsEvaluationPreview} />
                ) : (
                  <p className="mb-0 profile-overview-empty-message">No published Table Topics Evaluations yet.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          )}

          {canViewPersonalProgressCards && (
          <>
          <Col xs={12} lg={4}>
            <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="profile-overview-card-top">
                  <h4 className="mb-0">{feedbackTitle}</h4>
                  <div className="profile-overview-card-header-actions">
                    {!sectionLoading.feedback && activeFeedback && (
                      <button
                        type="button"
                        className="profile-overview-button"
                        onClick={() => goToPathwayProject(activeFeedback, 'overview-feedback-card')}
                        style={{ fontSize: '0.82rem', padding: '0.2rem 0.42rem' }}
                      >
                        View <i className="bi bi-chevron-right" />
                      </button>
                    )}
                    <CardReloadButton
                      loading={sectionLoading.feedback}
                      onClick={() => reloadOverviewSection('feedback')}
                      label="feedback"
                    />
                  </div>
                </div>
                <div className="profile-overview-divider" />
                {sectionLoading.feedback ? (
                  <div className="profile-overview-card-loader">
                    <span className="profile-overview-loader-line profile-overview-loader-line--w88"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w72"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w95"></span>
                  </div>
                ) : (
                  <>
                    {showFeedbackPager && (
                      <div className="profile-overview-assignment-nav mb-2">
                        <button
                          type="button"
                          className="profile-overview-assignment-nav-btn"
                          onClick={goPrevFeedback}
                          aria-label="Previous feedback"
                        >
                          <i className="bi bi-chevron-left" />
                        </button>
                        <span className="profile-overview-assignment-nav-count">
                          {activeFeedbackIndex + 1} / {feedbackCards.length}
                        </span>
                        <button
                          type="button"
                          className="profile-overview-assignment-nav-btn"
                          onClick={goNextFeedback}
                          aria-label="Next feedback"
                        >
                          <i className="bi bi-chevron-right" />
                        </button>
                      </div>
                    )}
                    {activeFeedback ? (
                      <>
                        <h5 className='mb-1'>{normalizeText(activeFeedback?.attempt?.speechTitle || activeFeedback?.projectTitle || 'Speech')}</h5>
                        <p className="profile-overview-meta mb-1">
                          {`${normalizeText(activeFeedback?.pathwayName || 'Pathway')}`}
                        </p>
                        <p>{`${normalizeText(activeFeedback?.projectTitle || 'Project')}`}</p>
                        <p className="profile-overview-meta mb-2">{formatShortDate(activeFeedback?.attempt?.scheduledDate)}</p>
                        <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{activeFeedback.text}</p>
                      </>
                    ) : (
                      <p className="mb-0 profile-overview-empty-message">No feedback available yet.</p>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={4}>
            <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="profile-overview-card-top">
                  <h4 className="mb-0">Last Speech Evaluation</h4>
                  <div className="profile-overview-card-header-actions">
                    {!sectionLoading.evaluation && latestEvaluation && (
                      <button
                        type="button"
                        className="profile-overview-button"
                        onClick={() => goToPathwayProject(latestEvaluation, 'overview-evaluation-card')}
                        style={{ fontSize: '0.82rem', padding: '0.2rem 0.42rem' }}
                      >
                        View <i className="bi bi-chevron-right" />
                      </button>
                    )}
                    <CardReloadButton
                      loading={sectionLoading.evaluation}
                      onClick={() => reloadOverviewSection('evaluation')}
                      label="last speech evaluation"
                    />
                  </div>
                </div>
                <div className="profile-overview-divider" />
                {sectionLoading.evaluation ? (
                  <div className="profile-overview-card-loader">
                    <span className="profile-overview-loader-line profile-overview-loader-line--w86"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w82"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w95"></span>
                  </div>
                ) : (
                  <>
                    {latestEvaluation ? (
                      <>
                        <h5 className='mb-1'>{normalizeText(latestEvaluation?.attempt?.speechTitle || latestEvaluation?.projectTitle || 'Speech')}</h5>
                        <p className="profile-overview-meta mb-1">
                          {`${normalizeText(latestEvaluation?.pathwayName || 'Pathway')}`}
                        </p>
                        <p>{`${normalizeText(latestEvaluation?.projectTitle || 'Project')}`}</p>
                        <p className="profile-overview-meta mb-2"><i className="bi bi-asterisk" /> {formatShortDate(latestEvaluation?.attempt?.scheduledDate)} {normalizeText(latestEvaluation?.evaluation?.evaluatorName || '')}</p>
                        <ul className="profile-overview-evaluation-list mb-2">
                          {(Array.isArray(latestEvaluation?.evaluation?.topics) ? latestEvaluation.evaluation.topics : []).map((topic, idx) => (
                            <li key={`topic-${idx}`}>
                              <span>{normalizeEvaluationTopicName(topic?.name)}:</span>
                              <span>{renderStars(topic?.stars)}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{normalizeText(latestEvaluation?.evaluation?.feedback || 'No written summary provided.')}</p>
                      </>
                    ) : (
                      <p className="mb-0 profile-overview-empty-message">No evaluation available yet.</p>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={4}>
            <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="profile-overview-card-top">
                  <h4>{pathwayProjectTitle}</h4>
                  <CardReloadButton
                    loading={sectionLoading.pathwayProjects}
                    onClick={() => reloadOverviewSection('pathwayProjects')}
                    label="next pathway project"
                  />
                </div>
                <div className="profile-overview-divider" />
                {sectionLoading.pathwayProjects ? (
                  <div className="profile-overview-card-loader">
                    <span className="profile-overview-loader-line profile-overview-loader-line--w84"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w78"></span>
                    <span className="profile-overview-loader-line profile-overview-loader-line--w66"></span>
                  </div>
                ) : (
                  <>
                    {activePathwayProject ? (
                      <>
                        {showPathwayPager && (
                          <div className="profile-overview-assignment-nav mb-2">
                            <button type="button" className="profile-overview-assignment-nav-btn" onClick={goPrevPathwayProject} aria-label="Previous project">
                              <i className="bi bi-chevron-left" />
                            </button>
                            <span className="profile-overview-assignment-nav-count">
                              {activePathwayProjectIndex + 1} / {remainingPathwayProjects.length}
                            </span>
                            <button type="button" className="profile-overview-assignment-nav-btn" onClick={goNextPathwayProject} aria-label="Next project">
                              <i className="bi bi-chevron-right" />
                            </button>
                          </div>
                        )}
                        <p className="mb-1 profile-overview-meta">{activePathwayProject.pathwayName}</p>
                        <p className="mb-1">
                          <strong>Level {activePathwayProject.levelNo}</strong>
                          {activePathwayProject.levelTitle ? ` – ${activePathwayProject.levelTitle}` : ''}
                        </p>
                        {activePathwayProject?.suggestionMode === 'optional-electives' && (
                          <p className="mb-2 profile-overview-meta" style={{ fontSize: '0.78rem' }}>
                            Optional electives remaining
                          </p>
                        )}
                        <p className="mb-2 profile-overview-meta" style={{ fontSize: '0.82rem' }}>
                          Mandatory: {activePathwayProject.mandatoryRemaining} left &nbsp;·&nbsp; Elective: {activePathwayProject.electiveRemaining} left
                        </p>
                        <h5 className="profile-overview-subtitle mb-2">{activePathwayProject.projectTitle}</h5>
                        {(activePathwayProject.green || activePathwayProject.amber || activePathwayProject.red) && (
                          <div className="profile-overview-timers mb-2">
                            {activePathwayProject.green && <span className="profile-overview-timer profile-overview-timer--green">{activePathwayProject.green}</span>}
                            {activePathwayProject.amber && <span className="profile-overview-timer profile-overview-timer--amber">{activePathwayProject.amber}</span>}
                            {activePathwayProject.red && <span className="profile-overview-timer profile-overview-timer--red">{activePathwayProject.red}</span>}
                          </div>
                        )}
                        {activePathwayProject.projectDescription ? (
                          <p className="mb-2" style={{ fontSize: '0.86rem', opacity: 0.88 }}>
                            {activePathwayProject.projectDescription.length > 130
                              ? `${activePathwayProject.projectDescription.slice(0, 130)}…`
                              : activePathwayProject.projectDescription}
                          </p>
                        ) : null}
                        <div className="profile-overview-card-link-wrap mt-2">
                          <button type="button" className="profile-overview-button" onClick={() => setShowProjectModal(true)}>
                            View the project <i className="bi bi-arrow-right" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="profile-overview-empty-state">
                        <p className="mb-2 profile-overview-empty-state-title">No pathway assigned yet.</p>
                        <p className="mb-3 profile-overview-empty-state-text">
                          Select a pathway to unlock your next pathway projects here.
                        </p>
                        <div className="profile-overview-card-link-wrap">
                          <button type="button" className="profile-overview-button" onClick={goToPathwaySelection}>
                            Open {supportsPathwaysTab ? 'Pathways' : 'My Pathways'} tab <i className="bi bi-arrow-right" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          </>
          )}
        </Row>
      )}

      {activePathwayProject && (
        <StandardModal
          show={showProjectModal}
          handleClose={() => setShowProjectModal(false)}
          title={activePathwayProject.projectTitle}
          ModalBodyComponent={pathwayProjectModalBody}
          ModalFooterComponent={pathwayProjectModalFooter}
        />
      )}

      <StandardModal
        show={showAwardsModal}
        handleClose={() => setShowAwardsModal(false)}
        title="All Awards"
        ModalBodyComponent={awardsModalBody}
        ModalFooterComponent={awardsModalFooter}
      />

      {canViewTableTopicsEvaluations && (
      <StandardModal
        show={showTableTopicsEvaluationsModal}
        handleClose={() => setShowTableTopicsEvaluationsModal(false)}
        title="All Table Topics Evaluations"
        ModalBodyComponent={(
          <div className="d-flex flex-column gap-3">
            <input
              type="search"
              className="form-control"
              placeholder="Search by topic, meeting number, or meeting name"
              aria-label="Search Table Topics Evaluations"
              value={tableTopicsEvaluationsSearch}
              onChange={(event) => {
                setTableTopicsEvaluationsSearch(event.target.value);
                setTableTopicsEvaluations([]);
                setTableTopicsEvaluationsHasMore(false);
                setTableTopicsEvaluationsPage(0);
                setSectionLoading(current => ({ ...current, tableTopicsEvaluations: true }));
                setTableTopicsEvaluationsListError('');
              }}
            />
            {sectionLoading.tableTopicsEvaluations && (
              <div className="profile-overview-card-loader">
                <span className="profile-overview-loader-line profile-overview-loader-line--w86"></span>
                <span className="profile-overview-loader-line profile-overview-loader-line--w82"></span>
              </div>
            )}
            {!sectionLoading.tableTopicsEvaluations && tableTopicsEvaluationsListError && (
              <p className="mb-0 profile-overview-empty-message">{tableTopicsEvaluationsListError}</p>
            )}
            {tableTopicsEvaluations.map((evaluation) => (
              <Card key={`${evaluation.meetingId}-${evaluation.agendaItemId}`} className="text-start">
                <Card.Body>
                  <Link to={`/meeting/${evaluation.meetingId}`} className="h5 d-block mb-1">
                    {evaluation.meetingNumber ? `#${evaluation.meetingNumber} - ` : ''}
                    {normalizeText(evaluation.meetingTheme || 'Meeting')}
                  </Link>
                  <p className="profile-overview-meta mb-2">
                    {normalizeText(evaluation.meetingDate)} · {normalizeText(evaluation.speakerRole)}
                  </p>
                  <TableTopicsEvaluationDetails evaluation={evaluation} />
                </Card.Body>
              </Card>
            ))}
            {!sectionLoading.tableTopicsEvaluations && !tableTopicsEvaluationsListError && tableTopicsEvaluations.length === 0 && (
              <p className="mb-0 profile-overview-empty-message">
                {tableTopicsEvaluationsSearch.trim()
                  ? 'No Table Topics Evaluations match your search.'
                  : 'No published Table Topics Evaluations yet.'}
              </p>
            )}
            {tableTopicsEvaluationsHasMore && (
              <button
                type="button"
                className="profile-overview-button align-self-center"
                onClick={() => loadTableTopicsEvaluations({
                  page: tableTopicsEvaluationsPage + 1,
                  search: tableTopicsEvaluationsSearch,
                  append: true,
                })}
                disabled={tableTopicsEvaluationsLoadingMore || sectionLoading.tableTopicsEvaluations}
              >
                {tableTopicsEvaluationsLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
      />
      )}

      <StandardModal
        show={showAnnouncementModal}
        handleClose={() => setShowAnnouncementModal(false)}
        title={normalizeText(selectedOverviewAnnouncement?.title || 'Announcement')}
        ModalBodyComponent={announcementModalBody}
        ModalFooterComponent={announcementModalFooter}
      />
    </div>
  );
}